import { useState } from 'react';

/**
 * Universal card image component with fallback chain:
 * 1. Digital image (TCGdex/community)
 * 2. User's photo (their scan)
 * 3. Text placeholder with card info
 *
 * Props:
 * - digitalImage: clean digital art URL
 * - userPhoto: user's own photo URL
 * - name: card name (for placeholder)
 * - number: card number (for placeholder)
 * - setName: set name (for placeholder)
 * - preferPhoto: if true, show user photo first (for "photo" view mode)
 * - style: container style overrides
 * - imgStyle: img tag style overrides
 * - theme: theme object
 * - loading: "lazy" | "eager" (default "lazy")
 * - onClick: click handler
 */
export default function CardImage({
  digitalImage,
  userPhoto,
  name,
  number,
  setName,
  preferPhoto = false,
  style = {},
  imgStyle = {},
  theme,
  loading = 'lazy',
  onClick,
}) {
  const [imgError, setImgError] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  // Determine primary and fallback image
  const primary = preferPhoto
    ? (userPhoto || digitalImage)
    : (digitalImage || userPhoto);
  const fallback = preferPhoto
    ? (digitalImage)
    : (userPhoto);

  const showImage = !imgError && primary;
  const showFallback = imgError && fallback && !imgError;

  // If primary fails, try fallback
  const [useFallback, setUseFallback] = useState(false);
  const currentSrc = useFallback ? fallback : primary;
  const hasImage = currentSrc && !imgError;

  const handleError = () => {
    if (!useFallback && fallback && fallback !== primary) {
      setUseFallback(true);
    } else {
      setImgError(true);
    }
  };

  const containerStyle = {
    width: '100%',
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme?.bgCard || '#1a1a1a',
    ...style,
  };

  // Has an image to show
  if (hasImage) {
    return (
      <div style={containerStyle} onClick={onClick}>
        <img
          src={currentSrc}
          alt={name || 'Card'}
          loading={loading}
          decoding="async"
          onLoad={() => setImgLoaded(true)}
          onError={handleError}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: imgLoaded ? 1 : 0,
            transition: 'opacity 0.2s ease',
            ...imgStyle,
          }}
        />
      </div>
    );
  }

  // Text placeholder - no images available
  return (
    <div style={containerStyle} onClick={onClick}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 8,
        textAlign: 'center',
        width: '100%',
        height: '100%',
      }}>
        {name && (
          <span style={{
            color: theme?.text || '#fff',
            fontSize: 11,
            fontWeight: 700,
            lineHeight: 1.2,
            marginBottom: 2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            wordBreak: 'break-word',
          }}>
            {name}
          </span>
        )}
        {number && (
          <span style={{
            color: theme?.textSecondary || '#6a6a6a',
            fontSize: 9,
            fontWeight: 600,
          }}>
            #{number}
          </span>
        )}
        {setName && (
          <span style={{
            color: theme?.textSecondary || '#6a6a6a',
            fontSize: 8,
            lineHeight: 1.2,
            marginTop: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
          }}>
            {setName}
          </span>
        )}
        <span style={{
          color: theme?.textDisabled || '#4a4a4a',
          fontSize: 7,
          marginTop: 4,
          fontStyle: 'italic',
        }}>
          No image yet
        </span>
      </div>
    </div>
  );
}
